import { Component, ElementRef, AfterViewInit, ViewChild, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms'; 
import { Chart, registerables, ChartConfiguration } from 'chart.js';
import { Router, RouterModule } from '@angular/router';

Chart.register(...registerables);

type Period = 'today' | 'monthly' | 'yearly';

@Component({
  selector: 'app-profit-loss-sheet',
  standalone: true,
  imports: [CommonModule, FormsModule, RouterModule], 
  templateUrl: './profit-loss-sheet.component.html',
  styles: [`
    :host { display: block; background-color: #f8fafc; min-height: 100vh; }
    /*  scrollbar */
    ::-webkit-scrollbar { width: 6px; height: 6px; }
    ::-webkit-scrollbar-track { background: transparent; }
    ::-webkit-scrollbar-thumb { background: #d1d5db; border-radius: 3px; }
    ::-webkit-scrollbar-thumb:hover { background: #9ca3af; }
  `]
})
export class ProfitLossSheetComponent implements AfterViewInit, OnDestroy {
  activeSubTab: string = 'profits';
  
  periods: Period[] = ['today', 'monthly', 'yearly'];
  selectedPeriod: Period = 'monthly';
  
  @ViewChild('profitChart') private profitChartRef!: ElementRef;
  chart: any;

  // Goal Variables
  goalPercentage = 65;
  targetInput: number | null = null; 

  // KPI Data
  kpiStats = [
   { 
      label: 'Profit by Purchase Rate', 
      value: 124500.00, 
      type: 'currency',
      trend: '+4.2%', 
      icon: 'https://img.icons8.com/?size=48&id=87787&format=png&color=059669', 
      colorClass: 'text-emerald-600', 
      bgClass: 'bg-emerald-50' 
    },
    { 
      label: 'Profit by Ready Product', 
      value: 118200.50, 
      type: 'currency',
      trend: '+2.1%', 
      icon: 'M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z', 
      colorClass: 'text-yellow-600', 
      bgClass: 'bg-yellow-50' 
    },
    { 
      label: 'Profit by Assembly Line', 
      value: 135000.00, 
      type: 'currency',
      trend: '+5.5%', 
      icon: 'M13 7h8m0 0v8m0-8l-8 8-4-4-6 6', 
      colorClass: 'text-amber-600', 
      bgClass: 'bg-amber-50' 
    },
    { 
      label: 'Sale Count', 
      value: 142, 
      type: 'number',
      trend: '+12', 
      icon: 'M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z', 
      colorClass: 'text-gray-600', 
      bgClass: 'bg-gray-100' 
    }
  ];

  financialSummary = {
    revenue: 4500000.00,
    grossProfit: 850000.00,
    netProfit: 620000.00
  };

  ngAfterViewInit(): void {
    this.initChart();
  }

  ngOnDestroy(): void {
    if (this.chart) this.chart.destroy();
  }

  setPeriod(period: Period) {
    this.selectedPeriod = period;
    this.updateChartData();
  }

  // --- CHART LOGIC ---
  initChart() {
    if (!this.profitChartRef) return;
    
    const ctx = this.profitChartRef.nativeElement.getContext('2d');
    
    // Gold Gradient
    const gradient = ctx.createLinearGradient(0, 0, 0, 400);
    gradient.addColorStop(0, 'rgba(217, 119, 6, 0.25)');
    gradient.addColorStop(1, 'rgba(217, 119, 6, 0)');

    const config: ChartConfiguration = {
      type: 'line',
      data: {
        labels: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'],
        datasets: [{
          label: 'Net Profit',
          data: [12000, 19000, 3000, 5000, 2000, 3000, 45000, 21000, 35000, 42000, 55000, 62000],
          borderColor: '#d97706',
          backgroundColor: gradient,
          borderWidth: 2,
          pointBackgroundColor: '#ffffff',
          pointBorderColor: '#d97706',
          pointBorderWidth: 2,
          pointRadius: 5, 
          pointHoverRadius: 7,
          fill: true,
          tension: 0.4
        }]
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        layout: {
          padding: {
            top: 20,    
            right: 20,
            left: 10,
            bottom: 10
          }
        },
        plugins: {
          legend: { display: false },
          tooltip: {
            backgroundColor: '#1f2937',
            padding: 12,
            titleFont: { size: 13 },
            bodyFont: { size: 14, weight: 'bold' },
            cornerRadius: 8,
            displayColors: false,
            callbacks: {
              
              label: (context) => '₹ ' + context.parsed.y.toLocaleString('en-IN')
            }
          }
        },
        scales: {
          x: { 
            grid: { display: false }, 
            ticks: { color: '#6b7280', padding: 10 } 
          },
          y: { 
            border: { display: false }, 
            grid: { color: '#f3f4f6' }, 
           
            ticks: { 
              color: '#6b7280', 
              padding: 15, 
              callback: (val) => '₹' + val 
            } 
          }
        }
      }
    };

    this.chart = new Chart(ctx, config);
  }

  updateChartData() {
    if(this.chart) {
      this.chart.data.datasets[0].data = Array.from({length: 12}, () => Math.floor(Math.random() * 80000));
      this.chart.update();
    }
  }

  // --- GOAL UPDATE LOGIC ---
  updateMonthlyGoal() {
    if (this.targetInput && this.targetInput > 0) {
     
      
      const simulatedAchieved = Math.min(Math.floor((Math.random() * 40) + 50), 100); 
      this.goalPercentage = simulatedAchieved;
      
      // Reset input for cleaner UX
      this.targetInput = null;
    }
  }

  getCircleDashOffset(percentage: number) {
    const circumference = 2 * Math.PI * 45;
    return circumference - (percentage / 100) * circumference;
  }
}